<template>
    <div class="opModal selectQrCodesModal">
        <div class="d-flex justify-content-between align-items-center">
            <div class="mb-3">
                <h3 class="pageTitle mb-0">Seleziona QR Code</h3>
                <span class="text-muted small" v-if="$store.state.commesse.selectedCommessa">Commessa selezionata: {{ $store.state.commesse.selectedCommessa.name }}</span>
            </div>
        </div>
    
        <template v-if="!$store.state.isMobile">
            <hr class="my-2" />
            <div class="d-flex justify-content-between align-items-center my-3" >
                <div class="d-flex align-items-center gap-2">
                    <span class="badge fs-6" :class="selectedRows.length > 0 ? 'bg-primary' : 'bg-secondary'">
                        {{ selectedRows.length }} selezionati
                    </span>
                </div>
            </div>
        </template>
        
        <hr class="my-2" />

        <accordion label="Filtri" class="my-3">
            <qr-config-filters v-model="filterVm" />
        </accordion>

        <op-table :items="items" name="qrConfigs" :filterVm="filterVm" :total="total" v-if="!$store.state.isMobile">
            <template #header>
                <op-th class="justify-content-center" :width="100" :resizable="false" v-if="$store.state.isCommessaEditor()">
                    <button class="btn btn-sm btn-primary" @click="selectAll(items)">
                        Tutti <icon-check-mark />
                    </button>
                </op-th>
                <op-th width="10%" sort="createdOn">Data creazione</op-th>
                <op-th width="25%" sort="name">Nome</op-th>
                <op-th width="15%" sort="info">Info</op-th>
                <op-th width="25%" sort="isActive">Attivo</op-th>
            </template>

            <template #default="{ items }">
                <div class="op-row" style="position:relative" v-for="(item, i) in (items as OM.QrConfig[])" :key="item.identifier">
                    <div class="op-cell justify-content-center" v-if="$store.state.isCommessaEditor()">
                        <input type="checkbox" class="checkbox" v-model="selectedRows" :value="item" />
                    </div>
                    <div class="op-cell" :class="{ 'op-cell--wrap': $store.state.isCommessaEditor() }">
                        <span>{{$filters.date(item.createdOn)}}</span>
                        <small class="text-muted" v-if="$store.state.isCommessaEditor()">Creato da: <b>{{item.createdBy && item.createdBy.name}}</b></small>
                        <small class="text-muted" v-if="$store.state.isCommessaEditor()">Modificato da: <b>{{item.modifiedBy && item.modifiedBy.name}}</b></small>
                    </div>
                    <div class="op-cell op-cell--wrap">
                        <div class="fw-semibold text-truncate w-100">{{item.name}}</div>
                        <small class="text-muted text-truncate w-100" :title="item.description">{{item.description}}</small>
                    </div>
                    <div class="op-cell">{{item.info}}</div>
                    <div class="op-cell">
                        <icon-check-mark v-if="item.isActive" class="text-success"></icon-check-mark>
                    </div>
                </div>
            </template>

            <template #empty>Nessuna riga da mostrare</template>
        </op-table>

        <div class="mobileList" v-else>
            <div class="listItem" v-if="items.length == 0">
                <div class="listItemCell main">Nessuna riga da mostrare</div>
            </div>
            <div class="listItem" style="position:relative" v-for="(item, i) in items" :key="i">
                <div class="op-cell justify-content-center" v-if="$store.state.isCommessaEditor()">
                    <input type="checkbox" class="checkbox" v-model="selectedRows" :value="item" />
                </div>
                <div class="listItemCell main">
                    <div>{{item.name}}</div>
                    <div class="fs-12">{{item.description}}</div>
                </div>
                <div class="listItemCell small">
                    <icon-check-mark v-if="item.isActive" class="text-success"></icon-check-mark>
                </div>
            </div>
        </div>

        <div class="d-flex mt-4">
            <div class="form-group w-50 me-2">
                <label>Quantità per ogni QR selezionato</label>
                <input type="number" class="form-control" v-model="quantity" />
            </div>
            <div class="form-group w-50 ms-2">
                <label>Materiale per ogni QR selezionato</label>
                <select class="form-control" v-model="material">
                    <option value="">— Seleziona materiale —</option>
                    <option v-for="m in materials" :key="m.identifier" :value="m">{{ m.name }}</option>
                </select>
            </div>
        </div>

        <hr/>
        
        <div class="text-center">
            <button class="btn btn-primary" @click="confirmSelection">
                Conferma selezione
            </button>
            <button class="btn btn-danger-outline ms-2" @click="$emit('close')">
                Annulla
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import * as OM from '@/model';
import * as CONST from '@/const';
import { QrConfigClient } from '@/services/Services';
import { debounce } from '@/utils/debounce';
import { ModalServices } from '@/services/ModalServices';
import { Watch, Prop } from 'vue-property-decorator';
import store from '@/store';
import qrConfigFilters from '@/views/qrConfigs/components/qrConfigFilters.vue';
import { QrGenerator } from '@/services/QrGenerator';

@Options({ 
    components: { 
        qrConfigFilters 
    } 
})
export default class selectQrCodesModal extends Vue {

    @Prop() callback: (selectedRows: OM.QrConfig[], quantity: number, material: OM.NameIdentifier) => void;
    @Prop() materials: OM.NameIdentifier[];

    quantity: number = 1;
    material: OM.NameIdentifier = null;
    items: OM.QrConfig[] = [];
    total = 0;
    selectedRows: OM.QrConfig[] = [];
    filterVm: OM.QrConfigListFilterVm = new OM.QrConfigListFilterVm();

    created() {
        this.filterVm.paginationData.pageNumber = 1;
        this.filterVm.paginationData.perPage = 10;
        this.filterVm.isActive = null;
        this.filterVm.commessaIdentifier = store.state.commesse.selectedCommessa?.identifier || '';
    }

    @Watch('$store.state.selectedCommessa')
    onSelectedCommessaChange(val: OM.NameIdentifier) {
        this.filterVm.commessaIdentifier = val?.identifier || '';
    }

    init = debounce(() => {
        this.selectedRows = [];
        QrConfigClient.getByFilter(this.filterVm)
        .then(x => { 
            this.items = x.data;
            this.total = x.count;
        });
    }, 300)

    @Watch('filterVm', { deep: true })
    onFilterChange(){
        this.init();
    }

    getUrlIn(identifier: string){
        return QrGenerator.GetQrUrl(identifier);
    }

    selectAll(whichOnes){
        if(this.selectedRows.length == this.items.length){
            this.selectedRows = [];
        } else {
            this.selectedRows = [];
            whichOnes.forEach(x => { this.selectedRows.push(x); })
        }
    }

    confirmSelection(){
        this.callback(this.selectedRows, this.quantity, this.material);
        this.$emit('close');
    }

}
</script>
